import { useIdentity } from '@interhyp/platform-auth';
import { BaseLayout } from '@interhyp/platform-layout';
import { useModules, RemoteProvider, RemotesLoadingInfo } from '@interhyp/platform-module';
import { useApplicationContext } from '@interhyp/util-application-context';
import { Notification } from '@platform/elements';
import { memo, PropsWithChildren, ReactElement, useCallback, useEffect } from 'react';

import { useRemoteConfig } from '../hooks/useRemoteConfig';
import { LocalModule } from '../interfaces/LocalModule';

export const ModuleLoadingProvider = memo(({ children }: PropsWithChildren): ReactElement => {
  const { addModules } = useModules();

  const { hasPermissions } = useApplicationContext();

  const identity = useIdentity();

  const { modules, remotes } = useRemoteConfig({
    companyId: identity?.companyId,
    userType: identity?.userType,
  });

  const filterLocalModule = useCallback(
    ({ permissions, shouldRender }: LocalModule): boolean => {
      const hasPermissionsCheck = !permissions || hasPermissions?.(permissions);
      const shouldRenderCheck = shouldRender?.(identity) ?? true;

      return !!hasPermissionsCheck && shouldRenderCheck;
    },
    [hasPermissions, identity],
  );

  useEffect(() => {
    const filteredModules = modules.filter(filterLocalModule).map(({ module }) => module);

    addModules(filteredModules);
  }, [addModules, filterLocalModule, modules]);

  return (
    <RemoteProvider remotes={remotes} fallback={fallback}>
      {children}
    </RemoteProvider>
  );
});

function fallback({ status, error }: RemotesLoadingInfo): ReactElement {
  if (status === 'error') {
    return (
      <BaseLayout>
        <Notification type="error" title="Es ist ein Fehler beim laden der Module aufgetreten">
          <code>{JSON.stringify(error, null, 2)}</code>
        </Notification>
      </BaseLayout>
    );
  }

  return <></>;
}
